import { Autocomplete, TextField, Box, Typography } from '@mui/material';
import { getProductsThunk } from '@redux/features/product.slice';
import { addOfferThunk } from '@redux/features/offer.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { Product } from '@core/models/product.model';
import Modal from 'Common/Components/Modal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { useFormik } from 'formik';
import React, { FC, useEffect } from 'react';
import * as Yup from 'yup';
import { resolveUploadFileUrl } from 'helpers/resolveUploadFileUrl';

type IProps = {
    show: boolean;
    handleClose: () => void;
};

const AddOfferModal: FC<IProps> = ({ show, handleClose }) => {
    const { products } = useAppSelector((state: RootState) => state.productState);
    const { addOfferLoading } = useAppSelector((state: RootState) => state.offerState);
    // ----------------------------------------------------------------
    const dispatch = useAppDispatch();
    // --------------------------------------------------
    const validationSchema = Yup.object({
        title: Yup.string().required('Title Is Required'),
        productId: Yup.number()
            .required('Product Is Required')
            .min(1, 'Please select a product')
            .typeError('Please select a product'),
    });
    const handleSubmit = (values: any) => {
        const productId = typeof values.productId === 'string' ? Number(values.productId) : values.productId;
        dispatch(addOfferThunk({ title: values.title, productId })).then((res: any) => {
            if (res.meta.requestStatus === 'fulfilled') {
                handleClose();
                formik.resetForm();
            }
        });
    };
    // --------------------------------------------------
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            title: ``,
            productId: 0,
        },
        validationSchema,
        onSubmit: handleSubmit,
    });
    // --------------------------------------------------
    useEffect(() => {
        if (show) {
            dispatch(getProductsThunk({ page: 0, productSize: 1000 }));
        }
    }, [show, dispatch]);
    // --------------------------------------------------
    // Extend Product type to include productPhoto from API response
    type ProductWithPhoto = Product & {
        productPhoto?: {
            id: number | null;
            productId: number;
            photoPath: string;
        };
    };

    const productList: ProductWithPhoto[] = (products?.content || []) as ProductWithPhoto[];

    // Clear selection if selected product has priceOffer === 0
    useEffect(() => {
        if (formik.values.productId && formik.values.productId !== 0) {
            const selectedProduct = productList.find((p) => p.id === formik.values.productId);
            if (
                selectedProduct &&
                (selectedProduct.priceOffer === 0 ||
                    selectedProduct.priceOffer === null ||
                    selectedProduct.priceOffer === undefined)
            ) {
                formik.setFieldValue('productId', 0);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productList, formik.values.productId]);

    const getImageUrl = (photoPath: string | undefined | null) => resolveUploadFileUrl(photoPath);
    // --------------------------------------------------

    return (
        <Modal
            show={show}
            onHide={() => handleClose()}
            dialogClassName="w-full bg-white p-3 shadow rounded-md overflow-scroll md:w-[500px] md:max-h-[80vh]"
        >
            <Modal.Header>
                <Modal.Title className="text-lg font-semibold">Add Offer</Modal.Title>
            </Modal.Header>
            <Modal.Body className="my-5">
                <form onSubmit={formik.handleSubmit} className="flex flex-col gap-5">
                    <TextField
                        disabled={addOfferLoading}
                        fullWidth
                        size="small"
                        id="title"
                        name="title"
                        label="Offer Title"
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.title && Boolean(formik.errors.title)}
                        helperText={formik.touched.title && formik.errors.title}
                    />
                    <Autocomplete
                        disabled={addOfferLoading}
                        options={productList}
                        getOptionLabel={(option) => option.itemName || 'Unnamed Product'}
                        getOptionDisabled={(option) =>
                            option.priceOffer === 0 || option.priceOffer === null || option.priceOffer === undefined
                        }
                        value={(() => {
                            const selected = productList.find((p) => p.id === formik.values.productId);
                            // Only return the product if it's not disabled (priceOffer !== 0)
                            if (
                                selected &&
                                selected.priceOffer !== 0 &&
                                selected.priceOffer !== null &&
                                selected.priceOffer !== undefined
                            ) {
                                return selected;
                            }
                            return null;
                        })()}
                        onChange={(_, newValue) => {
                            // Prevent selecting products with priceOffer === 0
                            if (
                                newValue &&
                                (newValue.priceOffer === 0 ||
                                    newValue.priceOffer === null ||
                                    newValue.priceOffer === undefined)
                            ) {
                                return;
                            }
                            formik.setFieldValue('productId', newValue?.id || 0);
                        }}
                        onBlur={() => formik.setFieldTouched('productId', true)}
                        renderOption={(props, option) => {
                            const isDisabled =
                                option.priceOffer === 0 ||
                                option.priceOffer === null ||
                                option.priceOffer === undefined;
                            return (
                                <Box
                                    component="li"
                                    {...props}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        py: 1.5,
                                        px: 2,
                                        opacity: isDisabled ? 0.5 : 1,
                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                        '&:hover': {
                                            backgroundColor: isDisabled ? 'transparent' : 'action.hover',
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 50,
                                            height: 50,
                                            flexShrink: 0,
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            backgroundColor: 'grey.200',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {option.productPhoto?.photoPath ? (
                                            <img
                                                src={getImageUrl(option.productPhoto.photoPath)}
                                                alt={option.itemName || 'Product'}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                }}
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).style.display = 'none';
                                                }}
                                            />
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                No Image
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: 500,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                color: isDisabled ? 'text.disabled' : 'text.primary',
                                            }}
                                        >
                                            {option.itemName || 'Unnamed Product'}
                                            {isDisabled && (
                                                <Typography
                                                    component="span"
                                                    variant="caption"
                                                    color="error"
                                                    sx={{ display: 'block', mt: 0.5 }}
                                                >
                                                    (Not available - Price Offer is 0)
                                                </Typography>
                                            )}
                                        </Typography>
                                        {option.brandName && (
                                            <Typography
                                                variant="caption"
                                                color={isDisabled ? 'text.disabled' : 'text.secondary'}
                                                sx={{ display: 'block' }}
                                            >
                                                Brand: {option.brandName}
                                            </Typography>
                                        )}
                                        {option.unitPrice !== undefined && (
                                            <Typography
                                                variant="caption"
                                                color={isDisabled ? 'text.disabled' : 'text.secondary'}
                                                sx={{ display: 'block' }}
                                            >
                                                Price: ${option.unitPrice}
                                            </Typography>
                                        )}
                                        {option.priceOffer !== undefined && (
                                            <Typography
                                                variant="caption"
                                                color={isDisabled ? 'error.main' : 'text.secondary'}
                                                sx={{ display: 'block', fontWeight: isDisabled ? 600 : 400 }}
                                            >
                                                Price Offer: ${option.priceOffer}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Product"
                                size="small"
                                error={formik.touched.productId && Boolean(formik.errors.productId)}
                                helperText={formik.touched.productId && formik.errors.productId}
                            />
                        )}
                        sx={{ width: '100%' }}
                    />
                </form>
            </Modal.Body>
            <Modal.Footer className="flex items-center justify-center gap-2 mt-5">
                <ButtonBase
                    innerText="Cancel"
                    className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                    onClick={() => {
                        handleClose();
                        formik.resetForm();
                    }}
                    disabled={addOfferLoading}
                />
                <ButtonBase
                    innerText="Add Offer"
                    className="bg-yellow-600 hover:bg-yellow-800 rounded border-none px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={formik.handleSubmit}
                    disabled={addOfferLoading || !formik.values.productId || !formik.values.title}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default AddOfferModal;
